import {
  useAuth,
  useLoginWithRedirect,
  useEntitlementsActions,
} from '@frontegg/react';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Outlet,
  useLocation,
  useNavigate,
  Navigate,
  // Link,
} from 'react-router-dom';
import { useAuthActions } from '@frontegg/react';
import { useQueryClient } from '@tanstack/react-query';
import { storeInLocal } from 'src/lib/storage';
// import { useUpdateCurrentPractitionerSettings } from 'src/hooks/settings';
// import { CheckCircle } from '@insight-health-ai/roseate/icons';
// import { Button, message } from '@insight-health-ai/roseate/components';
import {
  isEmbeddedEnvironment,
  isVimOAuthFlow,
  useEmbeddedAuthActions,
  useVimAuthActions,
} from 'src/hooks/embedded/login';
import { ErrorScreen } from 'src/components/errorScreen';
import {
  getCurrentPractitioner,
  getCurrentPractitionerEntitlements,
  getCurrentPractitionerSettings,
} from 'src/store/actions/practitioner.action';
import { authenticationStatus } from 'src/store/slices/auth.slice';
import { AppLoader } from 'src/components/AppLoader';
import { useFavicon, useHtmlTitle, useTenant } from 'src/hooks/misc';
import {
  getQuotaAction,
  getTimeQuotaAction,
} from 'src/store/actions/quota.action';
import { getLegalConfirmationAction } from 'src/store/actions/legalConfirmation.action';
import { analytics, RUM, /* logger, */ salesforceChat } from 'src/services';
import { getPreBuiltAdminTemplates } from 'src/modules/ambient_solo/store/actions/settings';
import { Onboarding } from 'src/pages/onboarding';

export const ProtectedRoute = () => {
  const { settingsOrgValue } = useSelector((state) => state?.appointmentState);
  const { user, isAuthenticated } = useAuth();
  const { loadEntitlements } = useAuthActions();
  const loginWithRedirect = useLoginWithRedirect();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isEntitledTo } = useEntitlementsActions();
  const queryClient = useQueryClient();

  const [entitlementsLoaded, setEntitlementsLoaded] = useState(false);
  const {
    currentPractitionerLoading,
    currentPractitioner,
    currentPractitionerEntitlements,
    currentPractitionerEntitlementsLoading,
    currentPractitionerSettings,
  } = useSelector((state) => state?.practitionerState);

  // const { mutateAsync: updateCurrentPractitionerSettings } =
  //   useUpdateCurrentPractitionerSettings();

  const [isLoggedInViaFrontegg, setIsLoggedInViaFrontegg] = useState(false);
  const blockAccess = useMemo(() => {
    if (entitlementsLoaded && isAuthenticated) {
      let isOpAiAccessBlocked = false;
      try {
        isOpAiAccessBlocked = isEntitledTo({
          featureKey: 'op-ai-access-blocked',
        }).isEntitled;
      } catch {
        // Ignore error
      }
      const hasOpPracticeProviderRole = user?.roles?.some(
        (role) => role.key === 'op-practice-provider'
      );
      if (isOpAiAccessBlocked && hasOpPracticeProviderRole) return 'op-ai';

      if (isLoggedInViaFrontegg) {
        let isCareConnectAccessBlocked = false;
        try {
          isCareConnectAccessBlocked = isEntitledTo({
            featureKey: 'care-connect-access-blocked',
          }).isEntitled;
        } catch {
          // Ignore error
        }
        if (isCareConnectAccessBlocked) return 'care-connect';
      }
    }
    return false;
  }, [
    isAuthenticated,
    entitlementsLoaded,
    isEntitledTo,
    isLoggedInViaFrontegg,
    user?.roles,
  ]);

  useLayoutEffect(() => {
    queryClient.clear();
  }, [user?.tenantId]);

  const tenant = useTenant();

  useFavicon({ tenant });
  useHtmlTitle({ tenant });

  const { embeddedLogin, error: embeddedError } = useEmbeddedAuthActions();
  const { vimLogin } = useVimAuthActions();

  useEffect(() => {
    const redirectUrl = localStorage.getItem('_REDIRECT_AFTER_LOGIN_');
    if (isAuthenticated && redirectUrl) {
      localStorage.removeItem('_REDIRECT_AFTER_LOGIN_');
      navigate(redirectUrl);
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem(
        '_REDIRECT_AFTER_LOGIN_',
        location.pathname + location.search
      );

      if (isVimOAuthFlow()) {
        vimLogin();
      } else if (isEmbeddedEnvironment()) {
        embeddedLogin();
      } else {
        setIsLoggedInViaFrontegg(true);
        loginWithRedirect();
      }
    } else {
      loadEntitlements({
        callback: () => {
          setEntitlementsLoaded(true);
        },
      });
      dispatch(getCurrentPractitioner());
      dispatch(getCurrentPractitionerSettings());
      dispatch(getCurrentPractitionerEntitlements());
      dispatch(authenticationStatus(true));
      dispatch(getQuotaAction());
      dispatch(getTimeQuotaAction());
      dispatch(getPreBuiltAdminTemplates());
      storeInLocal(`user_name`, user?.name);
      storeInLocal(`user_email`, user?.email);
      storeInLocal(`user_id`, user?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    if (tenant === 'OP') {
      dispatch(getLegalConfirmationAction());
    }
  }, [tenant]);

  useEffect(() => {
    if (
      isAuthenticated &&
      user?.id &&
      user?.name &&
      user?.email &&
      !currentPractitionerEntitlementsLoading
    ) {
      RUM.setUser({
        id: user.id,
        name: user.name,
        email: user.email,
        plan: currentPractitionerEntitlements?.plan_type || 'n/a',
      });
    }
  }, [
    isAuthenticated,
    user?.id,
    user?.name,
    user?.email,
    currentPractitionerEntitlementsLoading,
  ]);

  useEffect(() => {
    if (
      currentPractitioner &&
      currentPractitionerEntitlements &&
      isAuthenticated
    ) {
      const {
        org_name,
        intercom,
        practitioner_name,
        practitioner_first_name,
        practitioner_last_name,
      } = currentPractitioner;

      window.Intercom?.('update', {
        hide_default_launcher: !!intercom?.hide_default_launcher,
      });

      if (tenant === 'OP') {
        salesforceChat.init();
      }

      const integrations = {
        integrations: {
          Intercom:
            import.meta.env.VITE_ENABLE_INTERCOM_USER_HASH === 'true'
              ? intercom
              : {},
        },
      };
      analytics.identify(
        user?.id,
        {
          email: user?.email,
          name: user?.name,
          organization: org_name,
          full_name: practitioner_name,
          first_name: practitioner_first_name,
          last_name: practitioner_last_name,
          plan_type: currentPractitionerEntitlements.plan_type,
          company: {
            name: org_name,
            plan_type: currentPractitionerEntitlements.plan_type,
          },
        },
        integrations
      );
    }
  }, [
    currentPractitioner,
    isAuthenticated,
    user?.email,
    user?.id,
    user?.name,
    currentPractitionerEntitlements,
  ]);

  // const showNewLayoutMessage =
  //   currentPractitionerSettings &&
  //   !currentPractitionerSettings?.sidenav_preferences?.version;
  // useEffect(() => {
  //   if (
  //     isAuthenticated &&
  //     entitlementsLoaded &&
  //     !blockAccess &&
  //     showNewLayoutMessage
  //   ) {
  //     message.info({
  //       content: (
  //         <div className="flex items-center gap-2">
  //           <div className="text-primary text-lg">
  //             <CheckCircle />
  //           </div>
  //           <div className="flex flex-col text-left">
  //             <span className="font-medium">Welcome to the new layout!</span>
  //             <span className="text-xs text-gray-500">
  //               You can always switch back from{' '}
  //               <Link
  //                 to="/settings"
  //                 state={{
  //                   highlightElementSelector: '.layout-version-switcher',
  //                 }}
  //                 className="text-primary underline"
  //                 onClick={() => {
  //                   message.destroy('new-layout-welcome');
  //                   analytics.track(
  //                     'Clicked Settings Link in The New Layout Welcome Message',
  //                     {
  //                       email: user?.email,
  //                       currentVersion:
  //                         currentPractitionerSettings?.sidenav_preferences
  //                           ?.version,
  //                     }
  //                   );
  //                 }}
  //               >
  //                 Settings → General
  //               </Link>
  //               .
  //             </span>
  //           </div>
  //           <Button
  //             appearance="primary"
  //             size="sm"
  //             onClick={async () => {
  //               try {
  //                 await updateCurrentPractitionerSettings({
  //                   sidenav_preferences: {
  //                     ...currentPractitionerSettings?.sidenav_preferences,
  //                     version: 2,
  //                   },
  //                 });
  //               } catch (error) {
  //                 logger.error(
  //                   'Failed to update current practitioner settings from new layout welcome message',
  //                   {},
  //                   error
  //                 );
  //               }
  //               message.destroy('new-layout-welcome');
  //               window.dispatchEvent(new Event('LayoutV2.WelcomeMessageSeen'));
  //               analytics.track(
  //                 'Clicked Okay in The New Layout Welcome Message',
  //                 {
  //                   email: user?.email,
  //                   currentVersion:
  //                     currentPractitionerSettings?.sidenav_preferences?.version,
  //                 }
  //               );
  //             }}
  //           >
  //             Okay
  //           </Button>
  //         </div>
  //       ),
  //       duration: 0,
  //       icon: <></>,
  //       key: 'new-layout-welcome',
  //     });
  //     analytics.track('Viewed The New Layout Welcome Message', {
  //       email: user?.email,
  //       currentVersion:
  //         currentPractitionerSettings?.sidenav_preferences?.version,
  //     });
  //   }
  // }, [isAuthenticated, entitlementsLoaded, blockAccess, showNewLayoutMessage]);

  if (blockAccess) {
    return (
      <ErrorScreen
        className="h-screen"
        title="Access blocked"
        description={
          blockAccess === 'op-ai'
            ? "Opie AI isn't enabled for your account. Contact your OP account manager to get access."
            : 'You are not authorized to access this resource. Please contact your administrator.'
        }
      />
    );
  }

  if (embeddedError) {
    return (
      <ErrorScreen
        className="h-screen"
        title={embeddedError.title}
        description={embeddedError.message}
      />
    );
  }

  return isAuthenticated &&
    !currentPractitionerLoading &&
    currentPractitionerSettings &&
    entitlementsLoaded ? (
    settingsOrgValue?.onboarding_complete != undefined &&
    !settingsOrgValue?.onboarding_complete ? (
      <>
        {/* Navigate to onboarding just for representation */}
        <Navigate to="/onboarding" />
        <Onboarding />
      </>
    ) : (
      <Outlet />
    )
  ) : (
    <AppLoader />
  );
};
